import { createRouteSchema, createStopSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import {
  useCurrency,
  type Driver,
  type Message,
  type Route,
  type Stop,
  type Vehicle,
} from './shared';

const blankRoute = { name: '', vehicleId: '', driverId: '' };
const blankStop = { name: '', position: '', pickup: '', drop: '', fee: '' };

export function RoutesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const managing = can('transport.manage', schoolId);
  const [routeForm, setRouteForm] = useState(blankRoute);
  const [stopForm, setStopForm] = useState(blankStop);
  const [open, setOpen] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const routes = useQuery({
    queryKey: ['transport', schoolId, 'routes'],
    queryFn: () =>
      apiList<Route>(`/schools/${schoolId}/transport/routes`, { query: { pageSize: 100 } }),
  });
  const vehicles = useQuery({
    queryKey: ['transport', schoolId, 'vehicles'],
    queryFn: () =>
      apiList<Vehicle>(`/schools/${schoolId}/transport/vehicles`, { query: { pageSize: 100 } }),
    enabled: managing,
  });
  const drivers = useQuery({
    queryKey: ['transport', schoolId, 'drivers'],
    queryFn: () =>
      apiList<Driver>(`/schools/${schoolId}/transport/drivers`, { query: { pageSize: 100 } }),
    enabled: managing,
  });
  const detail = useQuery({
    queryKey: ['transport', schoolId, 'route', open],
    queryFn: () => api<{ stops: Stop[] }>(`/schools/${schoolId}/transport/routes/${open}`),
    enabled: Boolean(open),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['transport', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const addRoute = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/transport/routes`, { method: 'POST', body }),
    onSuccess: () => {
      setRouteForm(blankRoute);
      setMessage({ kind: 'ok', text: t('tr.routeAdded') });
      refresh();
    },
    onError: fail,
  });
  const addStop = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/transport/routes/${open}/stops`, { method: 'POST', body }),
    onSuccess: () => {
      setStopForm(blankStop);
      setMessage({ kind: 'ok', text: t('tr.stopAdded') });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('tr.tab.routes')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {routes.data?.data.length === 0 && <p className="text-slate-600">{t('tr.noRoutes')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {routes.data?.data.map((r) => (
          <li key={r.id} className="space-y-2 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="font-medium">{r.name}</span>
                {r.status === 'inactive' && (
                  <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">
                    {t('tr.status.inactive')}
                  </span>
                )}{' '}
                · {r.vehicleRegistration ?? t('tr.noVehicle')} · {r.driverName ?? t('tr.noDriver')}
              </span>
              <span className="flex items-center gap-3">
                <span>{t('tr.seatsTaken', { taken: r.seated, total: r.capacity ?? 0 })}</span>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-expanded={open === r.id}
                  aria-label={`${t('tr.stops')}: ${r.name}`}
                  onClick={() => setOpen(open === r.id ? '' : r.id)}
                >
                  {t('tr.stops')} ({r.stopCount})
                </button>
              </span>
            </div>
            {open === r.id && (
              <div className="space-y-3 rounded bg-slate-50 p-3">
                {detail.data?.stops.length === 0 && (
                  <p className="text-slate-600">{t('tr.noStops')}</p>
                )}
                <ol className="space-y-1">
                  {detail.data?.stops.map((s) => (
                    <li key={s.id} className="flex flex-wrap justify-between gap-2">
                      <span>
                        {s.position}. {s.name}
                        {s.pickupTime ? ` · ${t('tr.pickupAt', { time: s.pickupTime })}` : ''}
                        {s.dropTime ? ` · ${t('tr.dropAt', { time: s.dropTime })}` : ''}
                      </span>
                      <span>
                        {t('tr.perMonth', { amount: formatMinor(s.monthlyFeeMinor, currency) })}
                      </span>
                    </li>
                  ))}
                </ol>
                {managing && (
                  <form
                    noValidate
                    aria-label={t('tr.addStop')}
                    className="grid gap-3 sm:grid-cols-5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const feeMinor = parseMajor(stopForm.fee || '0', currency);
                      const parsed = createStopSchema.safeParse({
                        name: stopForm.name,
                        position: Number(stopForm.position),
                        pickupTime: stopForm.pickup || null,
                        dropTime: stopForm.drop || null,
                        monthlyFeeMinor: feeMinor,
                      });
                      const next: Record<string, string> = {};
                      if (!parsed.success)
                        for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
                      setErrors(next);
                      if (!parsed.success) return;
                      setMessage(null);
                      addStop.mutate(parsed.data);
                    }}
                  >
                    <TextField
                      label={t('tr.stopName')}
                      value={stopForm.name}
                      error={errors.name}
                      onChange={(e) => setStopForm({ ...stopForm, name: e.target.value })}
                    />
                    <TextField
                      label={t('tr.position')}
                      inputMode="numeric"
                      value={stopForm.position}
                      error={errors.position}
                      onChange={(e) => setStopForm({ ...stopForm, position: e.target.value })}
                    />
                    <TextField
                      type="time"
                      label={t('tr.pickup')}
                      value={stopForm.pickup}
                      error={errors.pickupTime}
                      onChange={(e) => setStopForm({ ...stopForm, pickup: e.target.value })}
                    />
                    <TextField
                      type="time"
                      label={t('tr.drop')}
                      value={stopForm.drop}
                      error={errors.dropTime}
                      onChange={(e) => setStopForm({ ...stopForm, drop: e.target.value })}
                    />
                    <TextField
                      label={t('tr.monthlyFee', { currency })}
                      inputMode="decimal"
                      value={stopForm.fee}
                      error={errors.monthlyFeeMinor}
                      onChange={(e) => setStopForm({ ...stopForm, fee: e.target.value })}
                    />
                    <div className="sm:col-span-5">
                      <button type="submit" className={primaryButton} disabled={addStop.isPending}>
                        {t('tr.addStop')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {managing && (
        <form
          noValidate
          aria-label={t('tr.addRoute')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createRouteSchema.safeParse({
              name: routeForm.name,
              vehicleId: routeForm.vehicleId || null,
              driverId: routeForm.driverId || null,
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            addRoute.mutate(parsed.data);
          }}
        >
          <TextField
            label={t('tr.routeName')}
            value={routeForm.name}
            error={errors.name}
            onChange={(e) => setRouteForm({ ...routeForm, name: e.target.value })}
          />
          <div>
            <label htmlFor="route-vehicle" className="block text-sm font-medium">
              {t('tr.vehicle')}
            </label>
            <select
              id="route-vehicle"
              className={selectClass}
              value={routeForm.vehicleId}
              onChange={(e) => setRouteForm({ ...routeForm, vehicleId: e.target.value })}
            >
              <option value="">{t('tr.none')}</option>
              {vehicles.data?.data
                .filter((v) => v.status === 'active')
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.registrationNumber} ({t('tr.capacity', { n: v.capacity })})
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label htmlFor="route-driver" className="block text-sm font-medium">
              {t('tr.driver')}
            </label>
            <select
              id="route-driver"
              className={selectClass}
              value={routeForm.driverId}
              onChange={(e) => setRouteForm({ ...routeForm, driverId: e.target.value })}
            >
              <option value="">{t('tr.none')}</option>
              {drivers.data?.data
                .filter((d) => d.status === 'active')
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.fullName}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={addRoute.isPending}>
              {t('tr.addRoute')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
